import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Chip, Grid, List, ListItem, Divider, CircularProgress } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Sidebar from '../landingPage/Sidebar';
import { PrimaryBreadcrumb } from '../../components/ui/breadcrumb';
import { PrimaryButton } from '../../components/ui/button';
import { FeatureList } from '../../components/ui/list';
import { ConnectorCard } from '../../components/ui/card';
import { getTemplateById } from '../../apis/templateAPI';
import { getDisplayNameFromConnector } from '../../utils/connectorUtils';

// Import image icons
import salesforceIcon from './imageIcons/SalesforceIcons.png';
import blueDBIcon from './imageIcons/BlueDBIcon.png';
import greenDBIcon from './imageIcons/GreenDBIcon.png';
import csvFileIcon from './imageIcons/csvFileIcon.png';
import middleIcon from './imageIcons/MiddleIcon.png';
import playIcon from './imageIcons/playIcon.png';

// Helper function to map API response to component format
const mapApiTemplateDetailToComponent = (apiTemplate) => {
  // Map connector types to source types
  const getSourceType = (connector) => {
    const connectorLower = (connector || '').toLowerCase();
    if (connectorLower.includes('salesforce') || connectorLower.includes('api') || connectorLower.includes('rest')) {
      return 'SaaS';
    }
    if (connectorLower.includes('csv') || connectorLower.includes('file')) {
      return 'File';
    }
    return 'Database';
  };

  // Map connector types to icon types
  const getIconType = (connector) => {
    const connectorLower = (connector || '').toLowerCase();
    if (connectorLower.includes('salesforce') || connectorLower.includes('api') || connectorLower.includes('rest')) {
      return 'cloud';
    }
    if (connectorLower.includes('csv') || connectorLower.includes('file')) {
      return 'file';
    }
    return 'database';
  };

  // Determine complexity based on entity and field mapping counts
  const getComplexity = (entityCount, fieldMappingCount) => {
    const total = (entityCount || 0) + (fieldMappingCount || 0);
    if (total <= 10) return 'Simple';
    if (total <= 25) return 'Medium';
    return 'Advanced';
  };

  // Generate features from entity mappings
  const generateFeatures = (entityMappings, fieldMappings) => {
    const features = [];
    if (entityMappings) {
      const entityNames = Object.keys(entityMappings);
      if (entityNames.length > 0) {
        features.push(`Extracts data from ${entityNames.join(', ')} entities`);
      }
    }
    if (fieldMappings && fieldMappings.length > 0) {
      features.push('Transforms data types and structures for compatibility');
      features.push('Loads data incrementally to minimize processing time');
    }
    features.push('Handles schema evolution and data type conversions automatically');
    return features;
  };

  // Generate configuration requirements
  const generateConfiguration = (sourceConnector, destinationConnector) => {
    const config = [];
    if (sourceConnector) {
      config.push(`${sourceConnector} connection credentials`);
    }
    if (destinationConnector) {
      config.push(`${destinationConnector} connection details`);
    }
    config.push('Entity and field selection');
    config.push('Sync schedule (optional)');
    return config;
  };

  // Generate pipeline flow details
  const generatePipelineFlow = (apiTemplate) => {
    const sourceEntities = apiTemplate.default_entity_mappings 
      ? Object.keys(apiTemplate.default_entity_mappings).join(', ')
      : 'N/A';
    
    return {
      source: {
        name: getDisplayNameFromConnector(apiTemplate.source_connector) || 'Source',
        details: `Entities: ${sourceEntities}`,
        icon: getIconType(apiTemplate.source_connector),
      },
      transform: {
        name: 'Data Mapping',
        details: 'Type conversion, Field mapping, Null handling',
      },
      destination: {
        name: getDisplayNameFromConnector(apiTemplate.destination_connector) || 'Destination',
        details: 'Data warehouse',
        icon: getIconType(apiTemplate.destination_connector),
      },
    };
  };

  return {
    id: apiTemplate.template_id,
    title: apiTemplate.name || '',
    description: apiTemplate.description || '',
    sourceType: getSourceType(apiTemplate.source_connector),
    complexity: getComplexity(
      apiTemplate.entity_count || 0,
      apiTemplate.field_mapping_count || 0
    ),
    sourceIcon: getIconType(apiTemplate.source_connector),
    destinationIcon: getIconType(apiTemplate.destination_connector),
    usageCount: 0, // API doesn't provide usage count
    isPopular: false, // API doesn't provide popularity
    features: generateFeatures(
      apiTemplate.default_entity_mappings,
      apiTemplate.default_field_mappings
    ),
    sourceConnector: {
      name: getDisplayNameFromConnector(apiTemplate.source_connector) || 'Source',
      description: getSourceType(apiTemplate.source_connector),
      icon: getIconType(apiTemplate.source_connector),
    },
    destinationConnector: {
      name: getDisplayNameFromConnector(apiTemplate.destination_connector) || 'Destination',
      description: 'Data Warehouse',
      icon: getIconType(apiTemplate.destination_connector),
    },
    configuration: generateConfiguration(
      apiTemplate.source_connector,
      apiTemplate.destination_connector
    ),
    pipelineFlow: generatePipelineFlow(apiTemplate),
    // Store original API data
    apiData: apiTemplate,
  };
};

// Template data - fallback data (in case API fails)
const TEMPLATE_DATA = {
  1: {
    id: 1,
    title: 'Salesforce to BigQuery',
    description: 'Sync Salesforce CRM data to Google BigQuery for analytics',
    sourceType: 'SaaS',
    complexity: 'Simple',
    sourceIcon: 'cloud',
    destinationIcon: 'database',
    usageCount: 1234,
    isPopular: true,
    features: [
      'Extracts data from Salesforce objects (Accounts, Contacts, Opportunities)',
      'Transforms data types and structures for BigQuery compatibility',
      'Loads data incrementally to minimize processing time',
      'Handles schema evolution and data type conversions automatically',
    ],
    sourceConnector: {
      name: 'Salesforce',
      description: 'CRM Platform',
      icon: 'cloud',
    },
    destinationConnector: {
      name: 'Google BigQuery',
      description: 'Data Warehouse',
      icon: 'database',
    },
    configuration: [
      'Salesforce credentials (OAuth or API key)',
      'BigQuery project and dataset',
      'Entity and field selection',
      'Sync schedule (optional)',
    ],
    pipelineFlow: {
      source: {
        name: 'Salesforce',
        details: 'Objects: Accounts, Contacts, Opportunities',
        icon: 'cloud',
      },
      transform: {
        name: 'Data Mapping',
        details: 'Type conversion, Field mapping, Null handling',
      },
      destination: {
        name: 'BigQuery',
        details: 'Dataset: salesforce_data',
        icon: 'database',
      },
    },
  },
  2: {
    id: 2,
    title: 'MySQL to Snowflake',
    description: 'Replicate MySQL database tables to Snowflake data warehouse',
    sourceType: 'Database',
    complexity: 'Medium',
    sourceIcon: 'database',
    destinationIcon: 'database',
    usageCount: 1234,
    isPopular: false,
    features: [
      'Extracts data from MySQL tables',
      'Transforms data for Snowflake compatibility',
      'Loads data incrementally',
      'Handles schema changes automatically',
    ],
    sourceConnector: {
      name: 'MySQL',
      description: 'Database',
      icon: 'database',
    },
    destinationConnector: {
      name: 'Snowflake',
      description: 'Data Warehouse',
      icon: 'database',
    },
    configuration: [
      'MySQL connection credentials',
      'Snowflake account and warehouse',
      'Table selection',
      'Sync schedule',
    ],
    pipelineFlow: {
      source: {
        name: 'MySQL',
        details: 'Tables: users, orders, products',
        icon: 'database',
      },
      transform: {
        name: 'Data Mapping',
        details: 'Type conversion, Field mapping',
      },
      destination: {
        name: 'Snowflake',
        details: 'Database: analytics_db',
        icon: 'database',
      },
    },
  },
  3: {
    id: 3,
    title: 'REST API to PostgreSQL',
    description: 'Fetch data from REST APIs and load into PostgreSQL tables',
    sourceType: 'SaaS',
    complexity: 'Simple',
    sourceIcon: 'cloud',
    destinationIcon: 'database',
    usageCount: 1234,
    isPopular: true,
    features: [
      'Fetches data from REST API endpoints',
      'Transforms JSON data for PostgreSQL compatibility',
      'Loads data into PostgreSQL tables',
      'Handles API pagination and rate limiting',
    ],
    sourceConnector: {
      name: 'REST API',
      description: 'API Platform',
      icon: 'cloud',
    },
    destinationConnector: {
      name: 'PostgreSQL',
      description: 'Database',
      icon: 'database',
    },
    configuration: [
      'REST API endpoint URL',
      'API authentication credentials',
      'PostgreSQL connection details',
      'Table mapping configuration',
    ],
    pipelineFlow: {
      source: {
        name: 'REST API',
        details: 'Endpoints: /users, /orders, /products',
        icon: 'cloud',
      },
      transform: {
        name: 'Data Mapping',
        details: 'JSON parsing, Type conversion, Field mapping',
      },
      destination: {
        name: 'PostgreSQL',
        details: 'Database: api_data',
        icon: 'database',
      },
    },
  },
  4: {
    id: 4,
    title: 'CSV File to Redshift',
    description: 'Import CSV files from S3 into Amazon Redshift',
    sourceType: 'File',
    complexity: 'Simple',
    sourceIcon: 'file',
    destinationIcon: 'database',
    usageCount: 1234,
    isPopular: true,
    features: [
      'Reads CSV files from S3 bucket',
      'Validates and cleans CSV data',
      'Loads data into Redshift tables',
      'Handles large file processing',
    ],
    sourceConnector: {
      name: 'CSV File',
      description: 'File Storage',
      icon: 'file',
    },
    destinationConnector: {
      name: 'Amazon Redshift',
      description: 'Data Warehouse',
      icon: 'database',
    },
    configuration: [
      'S3 bucket and file path',
      'CSV file format and delimiter',
      'Redshift cluster connection',
      'Table schema definition',
    ],
    pipelineFlow: {
      source: {
        name: 'CSV File',
        details: 'Files: sales_data.csv, customer_data.csv',
        icon: 'file',
      },
      transform: {
        name: 'Data Mapping',
        details: 'CSV parsing, Data cleaning, Type conversion',
      },
      destination: {
        name: 'Redshift',
        details: 'Database: analytics_warehouse',
        icon: 'database',
      },
    },
  },
  5: {
    id: 5,
    title: 'Salesforce to BigQuery',
    description: 'Sync Salesforce CRM data to Google BigQuery for analytics',
    sourceType: 'SaaS',
    complexity: 'Simple',
    sourceIcon: 'cloud',
    destinationIcon: 'database',
    usageCount: 1234,
    isPopular: false,
    features: [
      'Extracts data from Salesforce objects (Accounts, Contacts, Opportunities)',
      'Transforms data types and structures for BigQuery compatibility',
      'Loads data incrementally to minimize processing time',
      'Handles schema evolution and data type conversions automatically',
    ],
    sourceConnector: {
      name: 'Salesforce',
      description: 'CRM Platform',
      icon: 'cloud',
    },
    destinationConnector: {
      name: 'Google BigQuery',
      description: 'Data Warehouse',
      icon: 'database',
    },
    configuration: [
      'Salesforce credentials (OAuth or API key)',
      'BigQuery project and dataset',
      'Entity and field selection',
      'Sync schedule (optional)',
    ],
    pipelineFlow: {
      source: {
        name: 'Salesforce',
        details: 'Objects: Accounts, Contacts, Opportunities',
        icon: 'cloud',
      },
      transform: {
        name: 'Data Mapping',
        details: 'Type conversion, Field mapping, Null handling',
      },
      destination: {
        name: 'BigQuery',
        details: 'Dataset: salesforce_data',
        icon: 'database',
      },
    },
  },
  6: {
    id: 6,
    title: 'Salesforce to BigQuery',
    description: 'Sync Salesforce CRM data to Google BigQuery for analytics',
    sourceType: 'SaaS',
    complexity: 'Simple',
    sourceIcon: 'cloud',
    destinationIcon: 'database',
    usageCount: 1234,
    isPopular: false,
    features: [
      'Extracts data from Salesforce objects (Accounts, Contacts, Opportunities)',
      'Transforms data types and structures for BigQuery compatibility',
      'Loads data incrementally to minimize processing time',
      'Handles schema evolution and data type conversions automatically',
    ],
    sourceConnector: {
      name: 'Salesforce',
      description: 'CRM Platform',
      icon: 'cloud',
    },
    destinationConnector: {
      name: 'Google BigQuery',
      description: 'Data Warehouse',
      icon: 'database',
    },
    configuration: [
      'Salesforce credentials (OAuth or API key)',
      'BigQuery project and dataset',
      'Entity and field selection',
      'Sync schedule (optional)',
    ],
    pipelineFlow: {
      source: {
        name: 'Salesforce',
        details: 'Objects: Accounts, Contacts, Opportunities',
        icon: 'cloud',
      },
      transform: {
        name: 'Data Mapping',
        details: 'Type conversion, Field mapping, Null handling',
      },
      destination: {
        name: 'BigQuery',
        details: 'Dataset: salesforce_data',
        icon: 'database',
      },
    },
  },
};

const getIcon = (iconType, isDestination = false) => {
  const iconSize = { xs: 24, sm: 28, md: 32 };
  
  switch (iconType) {
    case 'cloud':
      return (
        <Box
          component="img"
          src={salesforceIcon}
          alt="Source"
          sx={{
            width: iconSize,
            height: iconSize,
            objectFit: 'contain',
          }}
        />
      );
    case 'database':
      return (
        <Box
          component="img"
          src={isDestination ? greenDBIcon : blueDBIcon}
          alt={isDestination ? 'Destination' : 'Source'}
          sx={{
            width: iconSize,
            height: iconSize,
            objectFit: 'contain',
          }}
        />
      );
    case 'file':
      return (
        <Box
          component="img"
          src={csvFileIcon}
          alt="File"
          sx={{
            width: iconSize,
            height: iconSize,
            objectFit: 'contain',
          }}
        />
      );
    default:
      return (
        <Box
          component="img"
          src={salesforceIcon}
          alt="Source"
          sx={{
            width: iconSize,
            height: iconSize,
            objectFit: 'contain',
          }}
        />
      );
  }
};

const TemplateDetail = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch template details from API
  useEffect(() => {
    const loadTemplate = async () => {
      if (!templateId) {
        setError('Template ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const apiTemplate = await getTemplateById(templateId);
        const mappedTemplate = mapApiTemplateDetailToComponent(apiTemplate);
        setTemplate(mappedTemplate);
      } catch (err) {
        console.error('Error fetching template:', err);
        const errorMessage = err.response?.data?.detail || 'Failed to load template';
        setError(errorMessage);
        toast.error(errorMessage);
        
        // Fallback to default template if available
        const fallbackTemplate = TEMPLATE_DATA[1];
        if (fallbackTemplate) {
          setTemplate(fallbackTemplate);
        }
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [templateId]);

  const handleUseTemplate = () => {
    if (!template) return;
    
    // Get template_id from API data or fallback to id
    const templateIdValue = template.apiData?.template_id || template.id;
    
    // Navigate to template setup page with template data
    navigate('/pre-made-templates/setup', {
      state: {
        template: template,
        templateId: templateIdValue,
      },
    });
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', background: '#FAFBFD' }}>
        <Sidebar />
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // Show error state
  if (error && !template) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', background: '#FAFBFD' }}>
        <Sidebar />
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
            <PrimaryBreadcrumb
              items={[
                {
                  label: 'Pre-Made Templates',
                  path: '/pre-made-templates',
                },
                {
                  label: 'Template Details',
                },
              ]}
              sx={{ mb: { xs: 2, sm: 3 } }}
            />
            <Paper
              elevation={1.5}
              sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                borderRadius: '4px',
                bgcolor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: '#EF4444',
                  mb: 2,
                }}
              >
                {error}
              </Typography>
              <PrimaryButton onClick={() => navigate('/pre-made-templates')}>
                Back to Templates
              </PrimaryButton>
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  }

  // Show template details
  if (!template) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#FAFBFD' }}>
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
          {/* Breadcrumbs */}
          <PrimaryBreadcrumb
            items={[
              {
                label: 'Pre-Made Templates',
                path: '/pre-made-templates',
              },
              {
                label: template.title,
              },
            ]}
            sx={{ mb: { xs: 2, sm: 3 } }}
          />

          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {/* Left Column - Template Details */}
            <Grid item xs={12} lg={7}>
              {/* Template Header Card */}
              <Paper
                elevation={1.5}
                sx={{
                  p: { xs: 2, sm: 2.5, md: 3 },
                  borderRadius: '4px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  mb: { xs: 3, sm: 4 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row', md: 'column' },
                    gap: { xs: 2, sm: 2.5, md: 3 },
                    alignItems: { xs: 'flex-start', sm: 'center', md: 'flex-start' },
                    mb: { xs: 2, sm: 2.5 },
                  }}
                >
                  {/* Icons */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 1, sm: 1.25, md: 1.5 },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 48, sm: 56, md: 64 },
                        height: { xs: 48, sm: 56, md: 64 },
                        borderRadius: '4px',
                        bgcolor: '#DBEAFE',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {getIcon(template.sourceIcon, false)}
                    </Box>
                    <Box
                      component="img"
                      src={middleIcon}
                      alt="Arrow"
                      sx={{
                        width: { xs: 20, sm: 22, md: 24 },
                        height: { xs: 20, sm: 22, md: 24 },
                        objectFit: 'contain',
                      }}
                    />
                    <Box
                      sx={{
                        width: { xs: 48, sm: 56, md: 64 },
                        height: { xs: 48, sm: 56, md: 64 },
                        borderRadius: '4px',
                        bgcolor: '#DCFCE7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {getIcon(template.destinationIcon, true)}
                    </Box>
                  </Box>

                  {/* Tags and Usage */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { xs: 1, sm: 1.25 },
                      flex: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip
                        label={template.sourceType}
                        size="small"
                        sx={{
                          bgcolor: '#0F172A',
                          color: '#FFFFFF',
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          height: { xs: 24, sm: 26, md: 28 },
                          borderRadius: '4px',
                        }}
                      />
                      <Chip
                        label={template.complexity}
                        size="small"
                        sx={{
                          bgcolor: '#10B981',
                          color: '#FFFFFF',
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          height: { xs: 24, sm: 26, md: 28 },
                          borderRadius: '4px',
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: { xs: 0, sm: 1 } }}>
                        <Box
                          component="img"
                          src={playIcon}
                          alt="Play"
                          sx={{
                            width: { xs: 14, sm: 16, md: 16 },
                            height: { xs: 14, sm: 16, md: 16 },
                            objectFit: 'contain',
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            fontWeight: 400,
                            fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                            color: '#3F3F3F',
                          }}
                        >
                          {template.usageCount.toLocaleString()} uses
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Title and Description */}
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                    lineHeight: 1.2,
                    color: '#000000',
                    mb: { xs: 1, sm: 1.25 },
                  }}
                >
                  {template.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    lineHeight: 1.5,
                    color: '#525252',
                  }}
                >
                  {template.description}
                </Typography>
             

    <Divider sx={{ my: { xs: 2, sm: 2.5, md: 3 } }} />
              {/* What this template does */}
              <FeatureList
                title="What this template does"
                items={template.features}
                sx={{ bgcolor: '#FFFFFF', p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: '4px' }}
              />
             </Paper>

              {/* Supported Connectors */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5, md: 3 },
                  borderRadius: '4px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  mb: { xs: 3, sm: 4 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                    lineHeight: 1.2,
                    color: '#000000',
                    mb: { xs: 1.5, sm: 2 },
                  }}
                >
                  Supported Connectors
                </Typography>
                <Box sx={{ display: 'flex', gap: { xs: 2, sm: 2.5, md: 3 }, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                        color: '#3F3F3F',
                        mb: { xs: 1, sm: 1.25 },
                      }}
                    >
                      Source
                    </Typography>
                    <ConnectorCard
                      icon={getIcon(template.sourceConnector.icon, false)}
                      label={template.sourceConnector.name}
                      description={template.sourceConnector.description}
                      iconBgColor="#DBEAFE"
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                        color: '#3F3F3F',
                        mb: { xs: 1, sm: 1.25 },
                      }}
                    >
                      Destination
                    </Typography>
                    <ConnectorCard
                      icon={getIcon(template.destinationConnector.icon, true)}
                      label={template.destinationConnector.name}
                      description={template.destinationConnector.description}
                      iconBgColor="#DCFCE7"
                    />
                  </Box>
                </Box>
              </Paper>

              {/* Configuration Required */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5, md: 3 },
                  borderRadius: '4px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  mb: { xs: 3, sm: 4 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                    lineHeight: 1.2,
                    color: '#000000',
                    mb: { xs: 1.5, sm: 2 },
                  }}
                >
                  Configuration Required
                </Typography>
                <List sx={{ p: 0 }}>
                  {template.configuration.map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        p: 0,
                        mb: { xs: 0.75, sm: 1 },
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 400,
                          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                          lineHeight: 1.5,
                          color: '#3F3F3F',
                          '&::before': {
                            content: '"• "',
                            color: '#3F3F3F',
                            fontWeight: 'bold',
                            mr: 0.5,
                          },
                        }}
                      >
                        {item}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Ready to get started */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5, md: 3 },
                  borderRadius: '4px',
                  bgcolor: '#0F172A',
                  border: '1px solid #1E293B',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    lineHeight: 1.5,
                    color: '#FFFFFF',
                    mb: { xs: 2, sm: 2.5 },
                  }}
                >
                  Ready to get started? This template will guide you through the setup process
                </Typography>
                <PrimaryButton
                  variant="contained"
                  onClick={handleUseTemplate}
                  sx={{
                    bgcolor: '#FFFFFF',
                    color: '#0F172A',
                    '&:hover': {
                      bgcolor: '#F9FAFB',
                    },
                  }}
                >
                  Use this Template
                </PrimaryButton>
              </Paper>
            </Grid>

            {/* Right Column - Pipeline Flow Preview */}
            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  position: { lg: 'sticky' },
                  top: { lg: 20 },
                  backgroundColor: '#FFFFFF',
                  borderRadius: '4px',
                  border: '1px solid #E5E7EB',
                  p: { xs: 2, sm: 2.5, md: 3 },
                  mb: { xs: 3, sm: 4 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                    lineHeight: 1.2,
                    color: '#000000',
                    mb: { xs: 0.5, sm: 0.75 },
                  }}
                >
                  Pipeline Flow Preview
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                    lineHeight: 1.4,
                    color: '#6B7280',
                    mb: { xs: 2, sm: 3 },
                  }}
                >
                  This is how your pipeline will look
                </Typography>

                {/* Pipeline Steps */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 },px: { xs: 2, sm: 4, md: 6 } }}>
                  {/* Source */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 0,
                      borderRadius: '4px',
                      background: 'linear-gradient(180deg, #EEF3FF 0%, #D6E4FF 100%)',
                      border: '2px solid #155DFC',
                      minHeight: { xs: 120, sm: 140, md: 160 },
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                      <Box
                        sx={{
                          width: { xs: 48, sm: 56, md: 64 },
                          height: { xs: 48, sm: 56, md: 64 },
                          borderRadius: '4px',
                          bgcolor: '#155DFC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {(() => {
                          const iconType = template.pipelineFlow.source.icon;
                          let iconSrc;
                          if (iconType === 'cloud') {
                            iconSrc = salesforceIcon;
                          } else if (iconType === 'database') {
                            iconSrc = blueDBIcon;
                          } else if (iconType === 'file') {
                            iconSrc = csvFileIcon;
                          } else {
                            iconSrc = salesforceIcon;
                          }
                          return (
                            <Box
                              component="img"
                              src={iconSrc}
                              alt="Source"
                              sx={{
                                width: { xs: 24, sm: 28, md: 32 },
                                height: { xs: 24, sm: 28, md: 32 },
                                objectFit: 'contain',
                                filter: 'brightness(0) invert(1)',
                              }}
                            />
                          );
                        })()}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.0625rem' },
                            lineHeight: 1.2,
                            color: '#000000',
                            mb: 0.5,
                          }}
                        >
                          Source
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                            lineHeight: 1.4,
                            color: '#3F3F3F',
                          }}
                        >
                          {template.pipelineFlow.source.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        borderTop: '1px solid #C7D2FE',
                        p: { xs: 2, sm: 2.5, md: 3 },
                        pt: { xs: 1.5, sm: 2, md: 2.5 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 400,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          lineHeight: 1.4,
                          color: '#6B7280',
                        }}
                      >
                        {template.pipelineFlow.source.details}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Arrow */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: { xs: -1, sm: -1.5 } }}>
                    <Box
                      sx={{
                        width: '2px',
                        height: { xs: 20, sm: 24 },
                        bgcolor: '#C7D2FE',
                      }}
                    />
                  </Box>

                  {/* Transform */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 0,
                      borderRadius: '4px',
                      background: 'linear-gradient(180deg, #F7EDFF 0%, #E8D5FF 100%)',
                      border: '2px solid #9810FA',
                      minHeight: { xs: 120, sm: 140, md: 160 },
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                      <Box
                        sx={{
                          width: { xs: 48, sm: 56, md: 64 },
                          height: { xs: 48, sm: 56, md: 64 },
                          borderRadius: '4px',
                          bgcolor: '#9810FA',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          component="img"
                          src={middleIcon}
                          alt="Transform"
                          sx={{
                            width: { xs: 24, sm: 28, md: 32 },
                            height: { xs: 24, sm: 28, md: 32 },
                            objectFit: 'contain',
                            filter: 'brightness(0) invert(1)',
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.0625rem' },
                            lineHeight: 1.2,
                            color: '#000000',
                            mb: 0.5,
                          }}
                        >
                          Transform
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                            lineHeight: 1.4,
                            color: '#3F3F3F',
                          }}
                        >
                          {template.pipelineFlow.transform.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        borderTop: '1px solid #DDD6FE',
                        p: { xs: 2, sm: 2.5, md: 3 },
                        pt: { xs: 1.5, sm: 2, md: 2.5 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 400,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          lineHeight: 1.4,
                          color: '#6B7280',
                        }}
                      >
                        {template.pipelineFlow.transform.details}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Arrow */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: { xs: -1, sm: -1.5 } }}>
                    <Box
                      sx={{
                        width: '2px',
                        height: { xs: 20, sm: 24 },
                        bgcolor: '#DDD6FE',
                      }}
                    />
                  </Box>

                  {/* Destination */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 0,
                      borderRadius: '4px',
                      background: 'linear-gradient(180deg, #E7FAF4 0%, #D1F5E8 100%)',
                      border: '2px solid #3C9564',
                      minHeight: { xs: 120, sm: 140, md: 160 },
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                      <Box
                        sx={{
                          width: { xs: 48, sm: 56, md: 64 },
                          height: { xs: 48, sm: 56, md: 64 },
                          borderRadius: '4px',
                          bgcolor: '#3C9564',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {(() => {
                          const iconType = template.pipelineFlow.destination.icon;
                          let iconSrc;
                          if (iconType === 'cloud') {
                            iconSrc = salesforceIcon;
                          } else if (iconType === 'database') {
                            iconSrc = greenDBIcon;
                          } else if (iconType === 'file') {
                            iconSrc = csvFileIcon;
                          } else {
                            iconSrc = greenDBIcon;
                          }
                          return (
                            <Box
                              component="img"
                              src={iconSrc}
                              alt="Destination"
                              sx={{
                                width: { xs: 24, sm: 28, md: 32 },
                                height: { xs: 24, sm: 28, md: 32 },
                                objectFit: 'contain',
                                filter: 'brightness(0) invert(1)',
                              }}
                            />
                          );
                        })()}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.0625rem' },
                            lineHeight: 1.2,
                            color: '#000000',
                            mb: 0.5,
                          }}
                        >
                          Destination
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                            lineHeight: 1.4,
                            color: '#3F3F3F',
                          }}
                        >
                          {template.pipelineFlow.destination.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        borderTop: '1px solid #A7F3D0',
                        p: { xs: 2, sm: 2.5, md: 3 },
                        pt: { xs: 1.5, sm: 2, md: 2.5 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 400,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          lineHeight: 1.4,
                          color: '#6B7280',
                        }}
                      >
                        {template.pipelineFlow.destination.details}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateDetail;
